import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from "react-router-dom";
import { PublicClientApplication, EventType, InteractionStatus } from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { msalConfig, loginRequest } from "./services/authConfig";
import { DataProvider, useData } from "./data/DataProvider";
import DelphiMark from "./components/DelphiMark";
import FeedbackWidget from "./components/FeedbackWidget";

// Pages
import Dashboard from "./pages/Dashboard";
import ProjectDetail from "./pages/ProjectDetail";
import PeopleDirectory from "./pages/PeopleDirectory";
import PersonDetail from "./pages/PersonDetail";
import BestFitFinder from "./pages/BestFitFinder";
import SwapCompare from "./pages/SwapCompare";
import WorkforcePlanner from "./pages/WorkforcePlanner";
import Placement from "./pages/Placement";
import DreamTeam from "./pages/DreamTeam";
import Hiring from "./pages/Hiring";
import AdminImport from "./pages/AdminImport";
import Assignments from "./pages/Assignments";
import ResourceAI from "./pages/ResourceAI";
import Financials from "./pages/Financials";
import Login from "./pages/Login";

// ---- MSAL setup ----
export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.addEventCallback((event) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
    event.payload &&
    event.payload.account
  ) {
    msalInstance.setActiveAccount(event.payload.account);
  }
});

// ---- Auth context ----
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function ProtectedRoute({ children, requiredRole }) {
  const { user } = useAuth();
  const data = useData();
  if (!user) return <Navigate to="/login" />;
  if (requiredRole === "admin" && !(data && data.isAdmin)) return <Navigate to="/" />;
  return children;
}

const TOOLS_PATHS = ["/finder", "/swap", "/dreamteam", "/placement"];

function NavBar() {
  const { user, logout } = useAuth();
  const data = useData();
  const isAdmin = !!(data && data.isAdmin);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);
  const location = useLocation();
  const onToolsPage = TOOLS_PATHS.some((p) => location.pathname.startsWith(p));

  // Close the Tools menu when clicking anywhere outside it.
  useEffect(() => {
    if (!toolsOpen) return;
    const close = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setToolsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [toolsOpen]);

  return (
    <nav className="navbar">
      <div className="nav-brand">
        <div className="nav-logo">
          <DelphiMark size={26} />
        </div>
        <div>
          <div className="nav-title">Delphi</div>
          <div className="nav-sub">by Hardaway Construction</div>
        </div>
      </div>
      <div className="nav-links">
        <NavLink to="/" end className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          Projects
        </NavLink>
        <NavLink to="/people" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          People
        </NavLink>
        <NavLink to="/planner" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          Planner
        </NavLink>
        <NavLink to="/assignments" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          Assignments
        </NavLink>
        <NavLink to="/financials" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          Financials
        </NavLink>
        <div className="nav-dropdown" ref={toolsRef}>
          <button className={`nav-link nav-dropdown-btn ${toolsOpen || onToolsPage ? "active" : ""}`} onClick={() => setToolsOpen(o => !o)}>
            Tools <span className={`nav-caret ${toolsOpen ? "open" : ""}`}>▾</span>
          </button>
          {toolsOpen && (
            <div className="nav-dropdown-menu" onClick={() => setToolsOpen(false)}>
              <NavLink to="/finder" className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}>Best-Fit Finder</NavLink>
              <NavLink to="/swap" className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}>Swap &amp; Compare</NavLink>
              <NavLink to="/dreamteam" className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}>Dream Team Maker</NavLink>
              <NavLink to="/placement" className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}>Candidate Placement</NavLink>
            </div>
          )}
        </div>
        <NavLink to="/hiring" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          Hiring
        </NavLink>
        <NavLink to="/resource" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Resource AI <span className="nav-beta">BETA</span>
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin/import" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
            Admin
          </NavLink>
        )}
      </div>
      <div className="nav-user">
        <div className="user-chip">
          <div className="user-info-text">
            <div className="user-name">{user?.name}</div>
            <div className="user-role">{isAdmin ? "Admin" : (data && data.isFinance) ? "Finance" : "Executive"}</div>
          </div>
          <div className="user-avatar">
            {user?.name ? user.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?"}
          </div>
        </div>
        <button className="btn-logout" onClick={logout}>Sign Out</button>
      </div>
    </nav>
  );
}

function AppInner() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (!instance.getActiveAccount() && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance, accounts]);

  if (inProgress !== InteractionStatus.None && !isAuthenticated) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'Work Sans', sans-serif", color: "#213469", fontSize: 18 }}>
        Signing you in…
      </div>
    );
  }

  const account = instance.getActiveAccount() || accounts[0] || null;
  const user = isAuthenticated && account
    ? { name: account.name || account.username, email: account.username }
    : null;

  const auth = {
    user,
    login: () => instance.loginRedirect(loginRequest),
    logout: () => instance.logoutRedirect(),
  };

  return (
    <AuthContext.Provider value={auth}>
      <DataProvider>
        <BrowserRouter>
          {auth.user && <NavBar />}
          {auth.user && <FeedbackWidget />}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/projects/:jobNumber" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/people" element={<ProtectedRoute><PeopleDirectory /></ProtectedRoute>} />
            <Route path="/people/:id" element={<ProtectedRoute><PersonDetail /></ProtectedRoute>} />
            <Route path="/finder" element={<ProtectedRoute><BestFitFinder /></ProtectedRoute>} />
            <Route path="/swap" element={<ProtectedRoute><SwapCompare /></ProtectedRoute>} />
            <Route path="/planner" element={<ProtectedRoute><WorkforcePlanner /></ProtectedRoute>} />
            <Route path="/assignments" element={<ProtectedRoute><Assignments /></ProtectedRoute>} />
            <Route path="/financials" element={<ProtectedRoute><Financials /></ProtectedRoute>} />
            <Route path="/resource" element={<ProtectedRoute><ResourceAI /></ProtectedRoute>} />
            <Route path="/placement" element={<ProtectedRoute><Placement /></ProtectedRoute>} />
            <Route path="/dreamteam" element={<ProtectedRoute><DreamTeam /></ProtectedRoute>} />
            <Route path="/hiring" element={<ProtectedRoute><Hiring /></ProtectedRoute>} />
            <Route path="/admin/import" element={<ProtectedRoute requiredRole="admin"><AdminImport /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </DataProvider>
    </AuthContext.Provider>
  );
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppInner />
    </MsalProvider>
  );
}